import axios, { AxiosRequestConfig } from "axios";
import services from "../services";
const BASE_URL = process.env.REACT_APP_API_URL || "/";

// Setup Axios Interceptors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response, config } = error;

    // If we receive a 401 Unauthorized from any endpoint other than /login,
    // it means the session has been invalidated (e.g. by a new login).
    if (response && response.status === 401 && !config.url?.endsWith("/login")) {
      console.warn("Session invalidated (401). Logging out automatically.");

      // Clear local session data
      services.auth.signOut();

      // Force redirect to sign-in page if not already there
      if (!window.location.pathname.startsWith("/sign-in")) {
        // Set flag for Sign-In page to show a notification after the reload
        localStorage.setItem("session_invalidated", "true");
        window.location.assign("/sign-in");
      }
    }

    return Promise.reject(error);
  }
);

interface VerbParamsInterface {
  url: string;
  body?: any;
  config?: AxiosRequestConfig;
}

const getConfig = async () => {
  const token = services.auth.getToken() || "";

  return {
    baseURL: BASE_URL,
    ...(token && { headers: { authorization: `Bearer ${token}` } }),
  };
};

const Get = async ({ url, config = {} }: VerbParamsInterface) => {
  const defaultConfig = await getConfig();
  const { data } = await axios.get(url, { ...defaultConfig, ...config });
  return data;
};

const Post = async ({ url, body = {}, config = {} }: VerbParamsInterface) => {
  const defaultConfig = await getConfig();
  const { data, status } = await axios.post(url, body, { ...defaultConfig, ...config });
  return { ...(data?.error ?? data), httpCode: status };
};

const Put = async ({ url, body = {}, config = {} }: VerbParamsInterface) => {
  const defaultConfig = await getConfig();
  const { data } = await axios.put(url, body, { ...defaultConfig, ...config });
  return data;
};

const Del = async ({ url, config = {} }: VerbParamsInterface) => {
  const defaultConfig = await getConfig();
  const { data } = await axios.delete(url, { ...defaultConfig, ...config });
  return data;
};

const Patch = async ({ url, body = {}, config = {} }: VerbParamsInterface) => {
  const defaultConfig = await getConfig();
  const { data } = await axios.patch(url, body, {
    ...defaultConfig,
    ...config,
  });
  return data;
};

const actions = {
  Get,
  Post,
  Put,
  Del,
  Patch,
};

export default actions;
